import React from "react";
import { Link } from "react-router-dom";
import { Trophy, Activity, Sparkles, ChevronRight } from "lucide-react";
import { getSkin, SkinPatternOverlay } from "./skins";
import RelPill from "./RelPill";
import { useT } from "../context/LanguageContext";

/**
 * V2 Profile / hero card with optional applied skin.
 * When `skinId` is null/falsy, renders the default purple→blue gradient hero.
 * When `skinId` matches a registered skin, swaps the bg/border/text colors but
 * keeps every metric (rank, points, GD, reliability, status, capacity) visible
 * and functional.
 *
 * Props mirror the existing hero shape so a drop-in replacement is trivial.
 */
export default function SkinnedProfileCard({
  user, league, me, myRank, isViewingMine, onPickerOpen, skinId,
  testId = "v2-league-hero",
}) {
  const { t } = useT();
  const skin = getSkin(skinId);
  const hasSkin = !!skin;
  const skinName = hasSkin ? t(`v2.skin.${skin.id}.label`) : null;

  // Default theme (no skin applied) — matches existing hero look.
  const theme = hasSkin ? {
    bg: skin.bg,
    border: skin.border,
    accent: skin.accent,
    text: skin.text,
    textMuted: skin.textMuted,
    chipBg: skin.accentSoft,
    chipText: skin.text,
    skinLabel: skinName,
    Icon: skin.icon,
    statBg: "rgba(0,0,0,0.35)",
    statText: skin.text,
    shadow: skin.shadow,
  } : {
    bg: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 35%, #3B82F6 100%)",
    border: "transparent",
    accent: "#FFFFFF",
    text: "#FFFFFF",
    textMuted: "rgba(255,255,255,0.8)",
    chipBg: "rgba(255,255,255,0.2)",
    chipText: "#FFFFFF",
    skinLabel: null,
    Icon: null,
    statBg: "rgba(255,255,255,0.1)",
    statText: "#FFFFFF",
    shadow: "0 25px 50px -12px rgba(124,58,237,0.3)",
  };

  return (
    <section
      data-testid={testId}
      data-skin={skinId || "default"}
      className="relative overflow-hidden rounded-3xl p-5"
      style={{
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        boxShadow: theme.shadow,
      }}
    >
      {/* Decorative pattern (skin-only) */}
      {hasSkin && <SkinPatternOverlay skinId={skinId} />}
      {/* Soft halo */}
      <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full pointer-events-none blur-2xl"
        style={{ backgroundColor: theme.chipBg }} />

      <div className="relative">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: theme.textMuted }}>
            {t("v2.my.season")}
          </span>
          <div className="flex items-center gap-1.5">
            {hasSkin && theme.Icon && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-[0.18em]" style={{ backgroundColor: theme.chipBg, color: theme.chipText }} data-testid={`v2-hero-skin-tag-${skinId}`}>
                <theme.Icon className="w-3 h-3" />
                {theme.skinLabel}
              </span>
            )}
            {user?.reliability_score != null && (
              <span data-testid="v2-hero-rel-pill"><RelPill score={user.reliability_score} flagged={user?.reliability_cache?.flagged} size="sm" /></span>
            )}
            {league?.status && (
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded-full" style={{ backgroundColor: theme.chipBg, color: theme.chipText }}>
                {league.status}
              </span>
            )}
          </div>
        </div>

        <h2 className="text-xl font-bold mt-1.5" style={{ color: theme.text }}>
          {onPickerOpen ? (
            <button
              type="button"
              onClick={onPickerOpen}
              data-testid="v2-hero-league-picker"
              className="inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity text-left"
              title={t("v2.my.picker.title")}
            >
              <span>{league?.name || t("v2.my.master")}</span>
              <ChevronRight className="w-4 h-4 rotate-90 opacity-80" />
            </button>
          ) : (
            <span>{league?.name || t("v2.my.master")}</span>
          )}
        </h2>
        <div className="text-xs mt-0.5 capitalize" style={{ color: theme.textMuted }}>
          {league?.platform} · {t("v2.my.players", { n: league?.player_count ?? 0, m: league?.capacity || 20 })}
          {!isViewingMine && <span className="ml-1 opacity-80">{t("v2.my.viewing")}</span>}
        </div>

        <div className="grid grid-cols-3 gap-3 mt-5">
          <Stat label={t("v2.my.rank")} value={myRank ? `#${myRank}` : "—"} testId="v2-stat-rank" theme={theme} />
          <Stat label={t("v2.my.points")} value={me?.points ?? 0} testId="v2-stat-points" theme={theme} />
          <Stat label={t("v2.my.gd")} value={me ? (me.gd > 0 ? `+${me.gd}` : me.gd) : 0} testId="v2-stat-gd" theme={theme} />
        </div>

        <Link
          to={league?.id ? `/v2/leagues/${league.id}` : "#"}
          data-testid="v2-view-league"
          className="mt-5 flex items-center justify-between rounded-2xl px-4 py-3 transition-colors"
          style={{ backgroundColor: theme.chipBg, color: theme.text }}
        >
          <span className="text-sm font-semibold">{t("v2.my.viewHub")}</span>
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

function Stat({ label, value, testId, theme }) {
  return (
    <div className="rounded-xl px-3 py-2.5 backdrop-blur" data-testid={testId} style={{ backgroundColor: theme.statBg }}>
      <div className="text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: theme.textMuted }}>{label}</div>
      <div className="text-lg font-bold mt-0.5" style={{ color: theme.statText }}>{value}</div>
    </div>
  );
}
